import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Calendar } from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import { EditorialMarquee, LogoNavy } from "@/components/Layout";
import { Radar4D, ScoreBars } from "@/components/Radar4D";
import { DIAG_URL } from "@/links";

const PAINS = [
  { n: "01", t: "Decisões sem baliza", d: "Ninguém sabe ao certo quais oportunidades perseguir e quais recusar." },
  { n: "02", t: "Preço em disputa", d: "Sem diferenciais comprovados, o fechamento depende de desconto e a margem encolhe." },
  { n: "03", t: "Times dizendo coisas diferentes", d: "Vendas, atendimento e marketing comunicam premissas divergentes. O resultado é atrito e retrabalho." },
  { n: "04", t: "Crescimento no braço", d: "O faturamento só avança pelo esforço do time comercial ou dos fundadores, sem demanda qualificada chegando sozinha." },
];

const STEPS = [
  { id: "D1", t: "Diagnóstico", q: "Onde estamos de verdade?", d: "Mapeamos negócio, marca, cliente, mercado e comunicação." },
  { id: "D2", t: "Direção", q: "Por que nos escolher, e para quem?", d: "Definimos posicionamento e proposta de valor." },
  { id: "D3", t: "Desenho", q: "Como isso é percebido e lembrado?", d: "Identidade visual e verbal com lastro estratégico." },
  { id: "D4", t: "Desdobramento", q: "Como colocamos isso para funcionar?", d: "Canais, jornada comercial e indicadores." },
];

const PILLARS = [
  { k: "Método", t: "Método próprio", d: "D1 a D4, cada etapa com entregáveis definidos e critérios objetivos de validação técnica." },
  { k: "Plataforma", t: "Plataforma digital exclusiva", d: "Área do cliente com painel operacional, marcos do cronograma, indicadores de evolução e radar estratégico." },
  { k: "Inteligência", t: "Agentes de IA da Fuchs", d: "Análise dos insumos do briefing em tempo reduzido. O time sênior fica com o julgamento crítico e a orientação da liderança." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMark = useTransform(scrollYProgress, [0, 1], [0, -140]);

  return (
    <section ref={ref} data-testid="home-hero" className="hero-glow noise relative overflow-hidden bg-ink">
      <div aria-hidden className="animate-float-slow pointer-events-none absolute -right-8 top-24 select-none">
        <motion.div
          style={{ y: yMark }}
          className="font-display text-[34vw] font-extrabold leading-none text-outline opacity-60 lg:text-[24vw]"
        >
          4D
        </motion.div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pb-36 lg:pt-48">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <SectionLabel>Posicionamento · Direção de marcas</SectionLabel>
          </motion.div>

          <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-[4.4rem]">
            {/* Estrutura mobile: 6 linhas equilibradas */}
            <span className="sm:hidden">
              <MaskedLine delay={0.25}>Sua empresa</MaskedLine>
              <MaskedLine delay={0.34}>chegou a um</MaskedLine>
              <MaskedLine delay={0.43}>
                <span className="font-serif font-medium italic tracking-normal text-brand-orange">novo momento.</span>
              </MaskedLine>
              <MaskedLine delay={0.52}>Sua marca</MaskedLine>
              <MaskedLine delay={0.61}>está pronta</MaskedLine>
              <MaskedLine delay={0.7}>para acompanhar?</MaskedLine>
            </span>
            {/* Estrutura desktop: 3 linhas */}
            <span className="hidden sm:block">
              <MaskedLine delay={0.25}>Sua empresa chegou</MaskedLine>
              <MaskedLine delay={0.38}>
                a um <span className="font-serif font-medium italic tracking-normal text-brand-orange">novo momento.</span>
              </MaskedLine>
              <MaskedLine delay={0.51}>
                Sua marca está pronta <span className="whitespace-nowrap">para acompanhar?</span>
              </MaskedLine>
            </span>
          </h1>

          <motion.div
            className="mt-8 h-px w-24 origin-left bg-brand-orange"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          />

          <motion.p
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
          >
            A FUCHS posiciona e dirige marcas para empresas em transformação, combinando estratégia,
            experiência e tecnologia por meio do Método 4D. Comece entendendo onde sua marca está hoje.
          </motion.p>

          <motion.p
            className="mt-5 font-mono text-[11px] uppercase tracking-[0.22em] text-brand-orange"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
          >
            Diagnóstico → Direção → Desenho → Desdobramento
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col gap-4 sm:flex-row"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <Link
              to={DIAG_URL}
              data-testid="hero-cta-diagnostico"
              className="group flex items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
            >
              Fazer o Diagnóstico Gratuito
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/metodo-4d"
              data-testid="hero-cta-metodo"
              className="group flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
            >
              Conhecer o Método 4D
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.p
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.3 }}
          >
            Receba gratuitamente seu Índice de Maturidade 4D e o Radar Estratégico da sua empresa.
          </motion.p>
        </div>
      </div>

      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Role</span>
        <motion.span
          className="h-10 w-px bg-gradient-to-b from-brand-orange to-transparent"
          animate={{ scaleY: [1, 0.4, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

const PainPoints = () => (
  <section data-testid="pain-points-section" className="relative bg-ink py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <SectionLabel>O problema</SectionLabel>
        <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Quando a marca fica para trás,{" "}
          <span className="font-serif font-medium italic text-brand-magenta">o negócio paga a diferença.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2">
        {PAINS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.08}>
            <div
              data-testid={`pain-card-${p.n}`}
              className="group h-full bg-ink p-8 transition-colors duration-500 hover:bg-navy lg:p-10"
            >
              <span className="font-mono text-xs text-brand-orange">{p.n}</span>
              <h3 className="mt-4 font-display text-xl font-bold text-white transition-colors group-hover:text-brand-orange sm:text-2xl">
                {p.t}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const MethodPreview = () => (
  <section data-testid="method-preview-section" className="bg-paper py-24 text-navy lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <LogoNavy className="mb-8" />
          <SectionLabel light>Método 4D</SectionLabel>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Quatro etapas, em ordem lógica.
          </h2>
        </div>
        <p className="max-w-sm font-serif text-lg italic text-navy/60">
          Nenhuma linha de design é traçada antes de a estratégia estar decidida.
        </p>
      </Reveal>

      <div className="mt-16 border-t border-navy/15">
        {STEPS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06}>
            <Link
              to="/metodo-4d"
              data-testid={`method-step-link-${s.id.toLowerCase()}`}
              className="group grid items-center gap-4 border-b border-navy/15 py-8 transition-colors duration-300 hover:bg-white md:grid-cols-12 md:gap-8 md:px-6"
            >
              <span className="font-display text-4xl font-extrabold text-navy/15 transition-colors duration-300 group-hover:text-brand-orange md:col-span-2 lg:text-5xl">
                {s.id}
              </span>
              <div className="md:col-span-4">
                <h3 className="font-display text-2xl font-bold">{s.t}</h3>
                <p className="mt-1 font-serif italic text-navy/55">{s.q}</p>
              </div>
              <p className="text-sm leading-relaxed text-navy/70 md:col-span-5">{s.d}</p>
              <ArrowRight
                className="hidden text-navy/30 transition-all duration-300 group-hover:translate-x-2 group-hover:text-brand-orange md:col-span-1 md:block"
                size={22}
              />
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12">
        <Link
          to="/metodo-4d"
          data-testid="method-preview-cta"
          className="group inline-flex items-center gap-3 border border-navy px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
        >
          Ver o Método 4D completo
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const RadarSection = () => (
  <section data-testid="radar-section" className="noise relative overflow-hidden bg-navy py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <Reveal>
            <SectionLabel>Diagnóstico de maturidade</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Comece pelo Diagnóstico de{" "}
              <span className="font-serif font-medium italic text-brand-orange">Maturidade Estratégica 4D.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
              Uma leitura objetiva da sua empresa, no lugar de conversas baseadas em impressão.
              É o ponto de partida do método.
            </p>
          </Reveal>
          <div className="mt-12 space-y-8">
            {[
              { t: "Índice de maturidade de 0 a 100", d: "Distribuído em Negócio, Marca, Cliente, Mercado e Marketing e Comunicação." },
              { t: "Radar estratégico visual", d: "Mostra os desequilíbrios e gargalos da organização em um único gráfico." },
              { t: "Relatório preliminar de diretrizes", d: "Leitura técnica dos gaps críticos e indicação da etapa recomendada." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08}>
                <div className="flex gap-5 border-l border-white/15 pl-6 transition-colors duration-300 hover:border-brand-orange">
                  <div>
                    <h3 className="font-display text-lg font-bold text-white">{f.t}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{f.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link
              to={DIAG_URL}
              data-testid="radar-cta-diagnostico"
              className="group inline-flex items-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-brand-red"
            >
              Fazer o diagnóstico agora
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="border border-white/10 bg-ink/60 p-6 backdrop-blur-sm lg:p-10">
            <Radar4D />
            <div className="mt-8 border-t border-white/10 pt-8">
              <ScoreBars />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section data-testid="pillars-section" className="bg-ink py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <SectionLabel>Ecossistema</SectionLabel>
        <h2 className="mt-6 max-w-3xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Método, plataforma e inteligência{" "}
          <span className="font-serif font-medium italic text-brand-magenta">trabalhando juntos.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.k} delay={i * 0.1}>
            <div
              data-testid={`pillar-card-${p.k.toLowerCase()}`}
              className="group relative h-full border border-white/10 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-orange/60 lg:p-10"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">{p.k}</span>
              <h3 className="mt-5 font-display text-2xl font-bold text-white">{p.t}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">{p.d}</p>
              <span className="absolute right-6 top-6 h-1.5 w-1.5 rounded-full bg-brand-magenta opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FounderSpotlight = () => (
  <section data-testid="founder-section" className="bg-paper py-24 text-navy lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
      <Reveal className="lg:col-span-5">
        <div className="relative mx-auto w-full max-w-[480px]">
          <div aria-hidden className="absolute -bottom-4 -right-4 h-full w-full border border-brand-magenta/50" />
          <div className="relative overflow-hidden">
            <img
              src="/assets/danielle-portrait.jpg"
              alt="Danielle Fuchs sentada em poltrona vermelha, de blazer azul-marinho"
              data-testid="founder-portrait"
              className="aspect-[2/3] w-full object-cover object-center transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal>
          <LogoNavy className="mb-8" />
          <SectionLabel light>A liderança</SectionLabel>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Danielle Fuchs é a inteligência{" "}
            <span className="font-serif font-medium italic text-brand-orange">por trás do método.</span>
          </h2>
        </Reveal>
        <div className="mt-10 flex gap-12">
          {[
            { v: "20+", l: "anos em decisões estratégicas" },
            { v: "100+", l: "empresas orientadas em posicionamento e crescimento" },
          ].map((s, i) => (
            <Reveal key={s.v} delay={i * 0.1}>
              <p className="font-display text-5xl font-extrabold text-brand-orange lg:text-6xl">{s.v}</p>
              <p className="mt-2 max-w-[180px] font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-navy/60">
                {s.l}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-10 max-w-xl text-base leading-relaxed text-navy/70">
            Antes de levar a Plataforma 4D aos clientes, a Fuchs aplicou o método na própria evolução.
            O reposicionamento da marca é o primeiro case.
          </p>
          <Link
            to="/quem-somos"
            data-testid="founder-cta-historia"
            className="group mt-8 inline-flex items-center gap-3 border border-navy px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
          >
            Conheça a história da Fuchs
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="home-final-cta" className="noise relative overflow-hidden bg-ink py-28 lg:py-40">
    <div aria-hidden className="pointer-events-none absolute inset-0 hero-glow" />
    <div className="relative mx-auto max-w-5xl px-6 text-center lg:px-10">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-orange">
          Diagnóstico de Maturidade Estratégica 4D
        </p>
        <h2 className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl">
          Veja onde a sua marca{" "}
          <span className="font-serif font-medium italic text-brand-orange">está hoje.</span>
        </h2>
        <div className="mt-12">
          <Link
            to={DIAG_URL}
            data-testid="final-cta-diagnostico"
            className="group inline-flex items-center gap-3 bg-brand-orange px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red"
          >
            Fazer o Diagnóstico de Maturidade 4D
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
        <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
          Prefere conversar antes?
        </p>
        <div className="mt-5 flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-start">
          <Link
            to="/agendar?tipo=venda"
            data-testid="final-cta-reuniao-estrategica"
            className="group inline-flex items-center justify-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand-orange hover:text-brand-orange"
          >
            <Calendar size={15} />
            Agendar Reunião Estratégica
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <Hero />
      <EditorialMarquee />
      <PainPoints />
      <MethodPreview />
      <RadarSection />
      <Pillars />
      <FounderSpotlight />
      <FinalCTA />
    </motion.main>
  );
}
